import {ParamComponent} from 'panel';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import ResizeObserver from 'resize-observer-polyfill';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {SegmentFilterController} from 'common/charts/contexts/segment-filter-controller';
import type {FormattedRow, LiftRow} from 'common/report/retention/util/types';
import {RetentionBookmarkParams} from 'common/report/retention/util/types';
import {RetentionQueryResponse} from 'common/report/retention/models/types';
import {getUiRowsData} from 'common/report/retention/util/parsing';
import {
  getBookmarkWithAppliedDefaults,
  getMetricDataTableColumns,
  getMetricDataTableRow,
  getMetricSegmentRowVal,
  getRetentionBucketName,
} from 'common/report/retention/util';
import {CurrentProject} from 'common/report/model-resources/projects';
import {MetricFormatType, MetricSegment} from 'common/charts/visualization/multi-metric/types';
import {ChartType, REPORT_LIMITS, ReportName} from 'common/report/constants';
import {flattenQueryResponse, retentionResponseHasData} from 'common/report/retention/util/request';
import {LiftType} from 'common/report/util/lift/types';
import {RetentionBookmarkParamsAndResults} from 'common/report/retention/charts/types';

import {template} from './template';
import css from './index.inline.styl';

export type RetentionTrendsMetricChartParams = {
  projectTimezone: string;
  limitDisplayedSegments?: boolean;
  segmentDisplayLimit?: number;
  paramsAndResults: RetentionBookmarkParamsAndResults;
};
interface MetricChartState {
  formattedUiRows: FormattedRow[];
  segmentRowsMapped: Map<string, FormattedRow>;
  comparisonSegmentRowsMapped: Map<string, FormattedRow>;
  liftRowsMapped: Map<string, LiftRow>;
}

interface RetentionTrendsMetricChartJsxProps {
  params: RetentionTrendsMetricChartParams;
}

export class RetentionTrendsMetricChart extends ParamComponent<
  RetentionTrendsMetricChartParams,
  MetricChartState,
  ChartsContextRegistry
> {
  private _data: RetentionQueryResponse;

  private _bookmarkParams: RetentionBookmarkParams;

  public chartResizeObserver: ResizeObserver;

  get config() {
    return {
      useShadowDom: true,
      css,
      contexts: [`segmentFilterController` as const, `currentProject` as const],

      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },

      defaultParams: {
        limitDisplayedSegments: false,
        segmentDisplayLimit: 0,
      },

      params: {
        projectTimezone: String,
        limitDisplayedSegments: Boolean,
        segmentDisplayLimit: Number,
        paramsAndResults: Object,
      },

      defaultState: {
        formattedUiRows: [],
        segmentRowsMapped: new Map(),
        comparisonSegmentRowsMapped: new Map(),
        liftRowsMapped: new Map(),
      },
      hooks: {
        preUpdate: (_newState: MetricChartState, newParams?: RetentionTrendsMetricChartParams) => {
          if (!newParams) return;
          if (newParams.paramsAndResults) {
            this.paramsAndResults = newParams.paramsAndResults;
          }
        },
      },

      template,
    };
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  set paramsAndResults(value: RetentionBookmarkParamsAndResults) {
    const updatedBookmarkParams = getBookmarkWithAppliedDefaults(value.params);
    const bookmarkChanged = !isEqual(this._bookmarkParams, updatedBookmarkParams);
    if (bookmarkChanged) {
      this._bookmarkParams = updatedBookmarkParams;
    }
    const dataChanged = !isEqual(this._data, value.results);
    if (dataChanged) {
      this._data = value.results;
    }
    if (bookmarkChanged || dataChanged) {
      this.updateSegmentRows();
      this.loadSegmentFilterController();
    }
  }

  get bookmarkParams() {
    return this._bookmarkParams;
  }

  getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  updateSegmentRows() {
    if (!this._data || !this._bookmarkParams) {
      return;
    }

    const {formattedUiRows, formattedComparisonRowsData, formattedLiftRowsData} = getUiRowsData(
      this._data,
      this._bookmarkParams,
      this.getProjectTimezone(),
    );

    const segmentRowsMapped = new Map(formattedUiRows.map((row) => [row.raw_head, row]));
    this.update({
      formattedUiRows,
      segmentRowsMapped,
      comparisonSegmentRowsMapped: formattedComparisonRowsData,
      liftRowsMapped: formattedLiftRowsData,
    });
  }

  loadSegmentFilterController() {
    const {segmentKeyToPath} = flattenQueryResponse(this._data, this._bookmarkParams.group_by);

    const segmentFilterItems = Array.from(this.state.segmentRowsMapped.values()).map((row) => {
      const comparisonRow = this.state.comparisonSegmentRowsMapped.get(row.raw_head);
      return {
        label: row.raw_head,
        id: row.raw_head,
        color: row.selection_color,
        tableRow: getMetricDataTableRow(row, segmentKeyToPath, this._bookmarkParams, comparisonRow),
      };
    });

    const maxNumSelections =
      this.params.segmentDisplayLimit > 0
        ? this.params.segmentDisplayLimit
        : REPORT_LIMITS[ReportName.Retention].segments[ChartType.RetentionTrendMetric].max;
    this.segmentFilterController.loadSections(
      [
        {
          items: segmentFilterItems,
          tableColumns: getMetricDataTableColumns(this._bookmarkParams),
        },
      ],
      {
        tableSortConfig: this._bookmarkParams.sorting,
        defaultNumSelections: REPORT_LIMITS[ReportName.Retention].segments[ChartType.RetentionTrendMetric].default,
        maxNumSelections,
      },
    );
    this.segmentFilterController.loadFromSavedLegend(
      this._bookmarkParams.selected_segments && [
        {selected: this._bookmarkParams.selected_segments.map((key) => ({key}))},
      ],
    );
    this.segmentFilterController.segmentationQueryLimit = REPORT_LIMITS[ReportName.Retention].query;
    this.segmentFilterController.exportSegmentationQueryLimit = REPORT_LIMITS[ReportName.Retention].csvExport;
  }

  connectedCallback() {
    if (this.params.paramsAndResults) {
      this.paramsAndResults = this.params.paramsAndResults;
    }
    super.connectedCallback();
  }
  hasResults = () => retentionResponseHasData(this._data);
  getProjectTimezone = () => this.params.projectTimezone || this.getCurrentProject()?.timezone;

  getMetricFormatter = () => {
    return this._bookmarkParams.percentage_view ? MetricFormatType.Percent : MetricFormatType.Number;
  };

  getMetricAttrs = (): MetricSegment[] => {
    const retentionBucketName = getRetentionBucketName({
      bucketIndex: this._bookmarkParams.retention_bucket,
      retentionType: this._bookmarkParams.retention_type,
      retentionUnit: this._bookmarkParams.retention_unit,
      dateUnit: this._bookmarkParams.date_unit,
      bucketSizes: this._bookmarkParams.bucket_sizes,
    });

    return this.segmentFilterController.getSelectedItems(0).map((item) => {
      const segmentRow = this.state.segmentRowsMapped.get(item.id);
      const comparisonRow = this.state.comparisonSegmentRowsMapped.get(item.id);
      const liftRow = this.state.liftRowsMapped.get(item.id);

      let {value, comparisonValue, liftValue} = getMetricSegmentRowVal(
        this._bookmarkParams.percentage_view,
        segmentRow,
        comparisonRow,
        liftRow,
      );

      let liftComparisonValue;
      if (this.bookmarkParams.lift_comparison) {
        liftComparisonValue = liftValue;
        if (this.bookmarkParams.lift_comparison.type === LiftType.Segment) {
          const baselineSegmentRow = this.state.segmentRowsMapped.get(this.bookmarkParams.lift_comparison.groupValue);
          const {value: newComparisonValue} = getMetricSegmentRowVal(
            this._bookmarkParams.percentage_view,
            baselineSegmentRow,
            comparisonRow,
            liftRow,
          );

          comparisonValue = newComparisonValue;
        }
      }

      return {
        label: segmentRow.head as string,
        color: segmentRow.selection_color,
        subLabel: retentionBucketName,
        ...(isNumber(comparisonValue) ? {comparisonValue} : {}),
        value,
        liftComparisonValue,
      };
    });
  };
}

export const RetentionTrendsMetricChartEl = defineAndMakeJsxFactory<RetentionTrendsMetricChartJsxProps>(
  `mp-retention-trends-metric-chart`,
  RetentionTrendsMetricChart,
);
export const RetentionTrendsMetricChartReact = panel2react<RetentionTrendsMetricChartJsxProps>(
  `mp-retention-trends-metric-chart`,
  RetentionTrendsMetricChart,
);
